import {Loader2} from "lucide-react";
import {useCallback, useEffect, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";

import ClaimAccountStep from "../components/claim-account-step";
import ClaimCodeStep from "../components/claim-code-step";
import ClaimPendingStep from "../components/claim-pending-step";
import ClaimPublicShell from "../components/claim-public-shell";
import ClaimUnavailablePanel from "../components/claim-unavailable-panel";
import useResidentClaimComplete from "../hooks/use-resident-claim-complete";
import useResidentClaimStart from "../hooks/use-resident-claim-start";
import {
  getClaimErrorMessage,
  getClaimFieldErrors,
  isClaimDisabledError,
  isClaimInvalidCodeError,
  isClaimRateLimitedError,
} from "../lib/claim-errors";
import {mapQueryCodeToClaimCode} from "../lib/map-query-code";
import type {ClaimAccountInput} from "../schemas/claim-account";
import type {ResidentClaimStartData} from "../types";

type ClaimWizardStep = "loading" | "code" | "account" | "pending" | "unavailable";

const PAGE_TITLE = "Activate your resident account";

export default function ResidentClaimPage() {
  const [searchParams] = useSearchParams();
  const autoStartAttempted = useRef(false);

  const [step, setStep] = useState<ClaimWizardStep>("code");
  const [claimCode, setClaimCode] = useState("");
  const [startData, setStartData] = useState<ResidentClaimStartData | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [pendingMessage, setPendingMessage] = useState("");
  const [codeErrorMessage, setCodeErrorMessage] = useState<string>();
  const [codeFieldError, setCodeFieldError] = useState<string>();
  const [accountErrorMessage, setAccountErrorMessage] = useState<string>();
  const [accountFieldErrors, setAccountFieldErrors] = useState<Record<string, string[]>>();
  const [unavailableMessage, setUnavailableMessage] = useState<string>();

  const startClaim = useResidentClaimStart();
  const completeClaim = useResidentClaimComplete();

  const handleStartError = useCallback((error: unknown) => {
    if (isClaimDisabledError(error)) {
      setUnavailableMessage(
        getClaimErrorMessage(error, "Resident claim is currently disabled."),
      );
      setStep("unavailable");
      return;
    }

    const fieldErrors = getClaimFieldErrors(error);
    setCodeFieldError(fieldErrors?.claim_code?.[0]);
    setCodeErrorMessage(
      getClaimErrorMessage(
        error,
        isClaimRateLimitedError(error)
          ? "Too many requests. Please try again later."
          : "Unable to validate claim code. Please try again.",
      ),
    );
    setStep("code");
  }, []);

  const runStartClaim = useCallback(
    (code: string) => {
      setClaimCode(code);
      setCodeErrorMessage(undefined);
      setCodeFieldError(undefined);
      setStep("loading");

      startClaim.mutate(
        {claim_code: code},
        {
          onSuccess: (data) => {
            setStartData(data);
            setStep("account");
          },
          onError: handleStartError,
        },
      );
    },
    [handleStartError, startClaim],
  );

  useEffect(() => {
    if (autoStartAttempted.current) return;

    const queryCode = mapQueryCodeToClaimCode(searchParams.get("code"));
    if (!queryCode) return;

    autoStartAttempted.current = true;
    runStartClaim(queryCode);
  }, [runStartClaim, searchParams]);

  const handleCodeSubmit = (code: string) => {
    runStartClaim(code);
  };

  const handleAccountSubmit = (values: ClaimAccountInput) => {
    setAccountErrorMessage(undefined);
    setAccountFieldErrors(undefined);

    completeClaim.mutate(
      {
        claim_code: claimCode,
        email: values.email,
        password: values.password,
      },
      {
        onSuccess: (result) => {
          setSubmittedEmail(values.email);
          setPendingMessage(
            result.message || "Check your email to confirm and activate your account.",
          );
          setStep("pending");
        },
        onError: (error) => {
          if (isClaimDisabledError(error)) {
            setUnavailableMessage(
              getClaimErrorMessage(error, "Resident claim is currently disabled."),
            );
            setStep("unavailable");
            return;
          }

          if (isClaimInvalidCodeError(error)) {
            setCodeErrorMessage(getClaimErrorMessage(error, ""));
            setStep("code");
            return;
          }

          setAccountFieldErrors(getClaimFieldErrors(error));
          setAccountErrorMessage(
            getClaimErrorMessage(error, "We could not complete your request. Please try again."),
          );
        },
      },
    );
  };

  // Fix pending message on success - I made a mistake above. The mutation onSuccess should use the API message from the mutation result. Let me fix - useMutation onSuccess receives (data, variables, context) where data is the return from mutationFn.

  return (
    <ClaimPublicShell title={PAGE_TITLE}>
      {step === "loading" ? (
        <div className="flex flex-col items-center gap-4 py-8">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Validating your claim code...</p>
        </div>
      ) : null}

      {step === "code" ? (
        <ClaimCodeStep
          onSubmit={handleCodeSubmit}
          isPending={startClaim.isPending}
          errorMessage={codeErrorMessage}
          fieldError={codeFieldError}
          initialCode={claimCode}
        />
      ) : null}

      {step === "account" && startData ? (
        <ClaimAccountStep
          claimCode={claimCode}
          startData={startData}
          onSubmit={handleAccountSubmit}
          isPending={completeClaim.isPending}
          errorMessage={accountErrorMessage}
          fieldErrors={accountFieldErrors}
          onBackToCode={() => {
            setAccountErrorMessage(undefined);
            setAccountFieldErrors(undefined);
            setStep("code");
          }}
        />
      ) : null}

      {step === "pending" ? (
        <ClaimPendingStep
          message={pendingMessage || "Check your email to confirm and activate your account."}
          email={submittedEmail}
        />
      ) : null}

      {step === "unavailable" ? (
        <ClaimUnavailablePanel message={unavailableMessage} />
      ) : null}
    </ClaimPublicShell>
  );
}
